import {
  Card,
  LinkButton,
  OutcomeCard,
  OUTCOMES,
  Shell,
  EvidenceBlock,
  QuestionChip,
  Pencil,
  Magnifier,
  Page as PageArt,
  OPERATOR_COMPANY,
  OPERATOR_ADDRESS,
  OPERATOR_UK_REP,
  OPERATOR_EMAIL,
} from "../../ui";
import { Link } from "react-router-dom";
import { Footer } from "./Footer";
import { PartnerStrip } from "../../ui/PartnerStrip";
import { MONTHLY, PACKS, VAT_LINE, priceOf } from "../../lib/plans";

export function HowItWorks() {
  return (
    <Shell chrome="marketing">
      <div className="paper">
        <section className="page section">
          <div style={{ display: "flex", alignItems: "center", gap: "var(--s-5)", marginBottom: "var(--s-6)" }}>
            <Pencil size={72} />
            <h1 style={{ maxWidth: "14ch" }}>How it works</h1>
          </div>
          <p className="lead measure">
            A photo of completed maths homework becomes a clear explanation and a useful next step. Built for parents, ready to work through together.
          </p>
        </section>

        <section className="page section" aria-labelledby="eyes-head">
          <div className="section-head">
            <h2 id="eyes-head">Understand the homework. Know how to help.</h2>
          </div>
          <div className="prose">
            <h3>The whole page, in one go</h3>
            <p>
              You photograph work your child has already done, and we read every answer on it. There is
              nothing to type in, no question to choose, and no worked solution to sit through. What you send
              is a page of your child's own handwriting, and what we mark is that page.
            </p>
            <h3>The report is written for you</h3>
            <p>
              You manage the account. Read the explanation yourself or sit with your child and work through the report, worked steps and practice together.
            </p>
            <h3>We tell you when we cannot mark it</h3>
            <p>
              We say when we cannot mark a page, we give you the reason, and that page costs nothing. We
              would rather say nothing than pick one cause and get it wrong.
            </p>
          </div>

          <Card tone="info" className="stack" style={{ marginTop: "var(--s-7)" }}>
            <h3 style={{ margin: 0 }}>When a read fails, and what fixes it</h3>
            <p>
              Most failed reads are the photograph rather than the page. A picture that is soft, or cropped,
              or that has already been squeezed by another app on the way here, does not hold enough detail
              for a model to read handwriting off it. Three things fix nearly all of them.
            </p>
            <ul className="prose">
              <li>One page per photo, edge to edge, with the whole page in the frame.</li>
              <li>
                Move the phone closer rather than pinching to zoom. Pinching crops the picture instead of
                magnifying it.
              </li>
              <li>
                Take it here rather than sending one on from a messaging app, because a message has already
                thrown detail away.
              </li>
            </ul>
          </Card>
        </section>

        <section className="page section" aria-labelledby="step-one">
          <div className="prose">
            <h2 id="step-one">1. You take the photo</h2>
            <p>
              Take a photo in the app or choose one from your photos. The app can
              measure what you sent. Before you spend anything we say whether there is enough detail in it,
              and if there is not you can retake it there and then.
            </p>

            <h2>2. We read it</h2>
            <p>
              The photo reaches our server and a model reads the handwriting for us. We work out the topic, we
              read the printed questions, and we check every answer we can. More complex pages can take longer, and
              reading carries on if you close the app.
            </p>
            <p>
              Where an answer is wrong, we try to name the mistake that produced it, and we only name one when a
              single mistake gives that answer exactly. Where more than one mistake would give it, we say so instead
              of guessing.
            </p>
            <Card tone="content" className="stack">
              <p style={{ fontWeight: 600, color: "var(--ink)" }}>What the evidence looks like</p>
              <EvidenceBlock wrote="1.000" answer="0.6" />
              <p className="small">
                Rounded to a whole number instead of to one significant figure. That mistake gives this answer
                exactly, which is why we are prepared to name it.
              </p>
              <div className="chipgrid" style={{ marginTop: "var(--s-2)" }}>
                <QuestionChip label="K" state="right" />
                <QuestionChip label="P" state="cause" />
                <QuestionChip label="G" state="no-cause" />
                <QuestionChip label="V" state="unread" />
                <QuestionChip label="R" state="right" />
              </div>
              <p className="small">
                A tick is right, a cross is wrong with the cause named, a dash is wrong with no cause found, and a
                dotted chip is a question we could not read. Never a letter, never a grade.
              </p>
            </Card>

            <h2>3. You pick one thing</h2>
            <p>
              The report names the mistake that repeats, shows what was written next to what the question wanted, and
              gives one thing to do this week. One thing, not a list.
            </p>
            <p>
              Follow the topic to relevant lessons, worksheets and practice. Opening practice in MathsHub costs no credit; external publishers set their own access terms. <Link to="/sources">Sources and licensing details</Link> explain what we show here and what opens on a publisher’s site.
            </p>
          </div>
        </section>

        <section className="page section" aria-labelledby="outcomes-head">
          <div className="section-head">
            <h2 id="outcomes-head">The five ways a page can turn out</h2>
            <p className="lead">
              All five look the same on the page, because telling you we could not mark something matters as
              much as telling you what we found. Three of them cost nothing: only a page we mark costs a
              credit.
            </p>
            <p className="lead">
              Credits come in packs, from {priceOf(PACKS[0]!)} for {PACKS[0]!.pages} pages up to{" "}
              {priceOf(PACKS[2]!)} for {PACKS[2]!.pages}, or {priceOf(MONTHLY)} a month for {MONTHLY.pages}{" "}
              pages. {VAT_LINE} <Link to="/pricing">What it costs, in full</Link>
            </p>
          </div>
          <div className="outcomes">
            {OUTCOMES.map((outcome) => (
              <OutcomeCard key={outcome} outcome={outcome} />
            ))}
          </div>
        </section>

        <section className="page section" aria-labelledby="confirm-head">
          <Card tone="surface" className="stack">
            <div style={{ display: "flex", alignItems: "center", gap: "var(--s-4)" }}>
              <Magnifier size={44} />
              <h2 id="confirm-head" style={{ margin: 0 }}>
                When we ask you to confirm
              </h2>
            </div>
            <p>
              Handwriting is hard to read, and sometimes we are not certain what a digit is. Rather than mark a
              page on a guess, we show you the few answers we are unsure about and you type what is actually on
              the paper.
            </p>
            <p>
              It takes under a minute. A page is only charged once, whether you confirm it or not, and what you
              type replaces our reading of it.
            </p>
          </Card>
        </section>

        <section className="page section">
          <Card tone="action" className="stack">
            <div style={{ display: "flex", alignItems: "center", gap: "var(--s-4)" }}>
              <PageArt size={44} />
              <h2 style={{ margin: 0 }}>One page is enough to start</h2>
            </div>
            <p style={{ color: "#E3E8EF" }}>
              An unmarked page is free, so the first one costs you nothing if we cannot read it.
            </p>
            <div style={{ maxWidth: "360px", marginTop: "var(--s-4)" }}>
              <LinkButton to="/capture" tone="secondary">
                Photograph a page
              </LinkButton>
            </div>
          </Card>
        </section>

        {/* Agent V5-C, wave 2. The host's partner list, live hosts only. */}
        <PartnerStrip />

        <Footer company={OPERATOR_COMPANY} address={OPERATOR_ADDRESS} rep={OPERATOR_UK_REP} email={OPERATOR_EMAIL} />
      </div>
    </Shell>
  );
}
